import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {InputTableApplyState} from 'analysis-tool/templates/AnalysisTemplateUtil';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import useStepTypeInfos, {StepTypeInfo} from 'pipelines/hooks/useStepTypeInfosQuery';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {getStepTypeIconOrDefault} from 'pipelines/StepIconUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {getOutputDataFrameName} from './OutputsUtil';
import ResultSchemaName from './ResultSchemaName';

export interface OutputsListProps {
  template: PipelineGraphQL;
  outputSteps: Array<GetAnalysisPipelineStep>;
  inputTables: Array<InputTableApplyState>;
  analysisSteps?: ReadonlyArray<PipelineStep>;
}

function getIconAndTitle(
  template: PipelineGraphQL,
  step: GetAnalysisPipelineStep,
  inputTables: Array<InputTableApplyState>,
  stepTypeInfos: ReadonlyMap<PipelineStepType, StepTypeInfo>,
  analysisSteps?: ReadonlyArray<PipelineStep>
): {
  icon: BenchlingDuotoneIconName | BenchlingIconName;
  title: string;
  subTitle: JSX.Element | string | null;
} {
  switch (step.definition.stepType) {
    case PipelineStepType.EXPORT_DATA_CATALOG_DATASET: {
      return {
        icon: 'dataset',
        title: getOutputDataFrameName(template, step, inputTables, analysisSteps),
        subTitle: 'As dataset',
      };
    }
    case PipelineStepType.SAVE_TO_RESULTS: {
      return {
        icon: 'table',
        title: getOutputDataFrameName(template, step, inputTables, analysisSteps),
        subTitle: (
          <>
            As {}
            <ResultSchemaName step={step} />
          </>
        ),
      };
    }
    default:
      return {
        icon: getStepTypeIconOrDefault(step.definition.stepType, stepTypeInfos, 'table'),
        title: getUserFacingStepTypeName(step.definition.stepType, stepTypeInfos),
        subTitle: null,
      };
  }
}

export default function OutputsList({
  template,
  outputSteps,
  inputTables,
  analysisSteps,
}: OutputsListProps): JSX.Element {
  const stepTypeInfos = useStepTypeInfos();
  const outputStepsWithoutSendToDocument = outputSteps.filter(
    (step) => step.definition.stepType !== PipelineStepType.SEND_TO_DOCUMENT
  );
  return (
    <section aria-label="Template outputs">
      {outputStepsWithoutSendToDocument.map((outputStep, index) => {
        const {icon, title, subTitle} = getIconAndTitle(
          template,
          outputStep,
          inputTables,
          stepTypeInfos,
          analysisSteps
        );
        return (
          <div
            key={`output-container-${index}`}
            className={css(styles.outputContainer)}
            {...getTestProps({component: 'OutputsList', element: 'output', index: String(index)})}
          >
            <BenchlingIcon icon={icon} />
            <span>{title}</span>
            <div />
            {subTitle && <span className={css(styles.subTitle)}>{subTitle}</span>}
          </div>
        );
      })}
    </section>
  );
}

const styles = StyleSheet.create({
  outputContainer: {
    ...FONT_PROXIMA_13,
    display: 'grid',
    gridTemplateColumns: '10px auto',
    columnGap: '6px',
    alignItems: 'center',
  },
  outputInfo: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 6,
  },
  subTitle: {
    fontStyle: 'italic',
    color: Colors.GRAY_DARK,
  },
});
